import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import { Container } from "reactstrap";
import ActionMenu from "../../../components/Common/ActionMenu";
import { get, del, post, patch as patchRequest } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";

/* -------------------------------------------------------------------------
 * AdminRestaurantsPage — full "Restaurants" table view with CRUD functionality.
 * Provides listing, search, sort, pagination, create, edit, and delete operations.
 * ---------------------------------------------------------------------- */

const getAccountStatusValue = (restaurant) => {
  const rawStatus = restaurant?.account_status?.toString().trim().toLowerCase();
  if (rawStatus === "active" || rawStatus === "inactive") return rawStatus;

  if (restaurant?.account_is_active === true) return "active";
  if (restaurant?.account_is_active === false) return "inactive";

  return null;
};

const getAccountStatusLabel = (accountStatus) => {
  if (accountStatus === "active") return "Active";
  if (accountStatus === "inactive") return "Inactive";
  return "Unknown";
};

const getNextAccountStatus = (accountStatus) => {
  if (accountStatus === "active") return "inactive";
  if (accountStatus === "inactive") return "active";
  return null;
};

const getAccountStatusConfirmation = (nextStatus) => ({
  title: nextStatus === "inactive" ? "Deactivate Restaurant Account" : "Activate Restaurant Account",
  message: nextStatus === "inactive"
    ? "Are you sure you want to deactivate this restaurant account? The restaurant will no longer be available for transaction matching, customer listings, new game progress, or Restaurant Portal access. Historical records will remain available."
    : "Are you sure you want to activate this restaurant account? Future transaction matching, customer visibility, new game eligibility, and Restaurant Portal access will be restored.",
  confirmText: nextStatus === "inactive" ? "Deactivate" : "Activate",
});

const isApiSuccess = (response) => {
  if (!response) return false;
  if (typeof response.data?.success !== "undefined") return response.data.success;
  if (typeof response.data?.status !== "undefined") return response.data.status;
  return response.status >= 200 && response.status < 300;
};

const extractPayload = (response) => {
  if (!response) return null;
  if (typeof response.data?.data !== "undefined") return response.data.data;
  return response.data || null;
};

const extractAccountStatusData = (response) => {
  const payload = extractPayload(response);
  if (!payload) return {};

  if (payload.account_status || typeof payload.account_is_active !== "undefined") return payload;
  if (payload.restaurant?.account_status || typeof payload.restaurant?.account_is_active !== "undefined") {
    return payload.restaurant;
  }
  if (payload.vendor_listing?.account_status || typeof payload.vendor_listing?.account_is_active !== "undefined") {
    return payload.vendor_listing;
  }

  return {};
};

const extractAccountStatusErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (responseData?.message) return responseData.message;
  if (typeof responseData?.error === "string" && responseData.error.trim()) return responseData.error;

  return extractErrorMessage(error) || "Unable to update the restaurant account status.";
};

const AdminRestaurantsPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Active Restaurants | Omaha Food Lovers";
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("restaurant_name");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [restaurantToDelete, setRestaurantToDelete] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [restaurantToArchive, setRestaurantToArchive] = useState(null);
  const [archiveAction, setArchiveAction] = useState(null); // 'archive' or 'unarchive'
  const [accountStatusTarget, setAccountStatusTarget] = useState(null);
  const [showAccountStatusModal, setShowAccountStatusModal] = useState(false);
  const [updatingAccountStatusId, setUpdatingAccountStatusId] = useState(null);
  const [showAccountSetupModal, setShowAccountSetupModal] = useState(false);
  const [restaurantToSetup, setRestaurantToSetup] = useState(null);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const emptyValue = "—";

  const formatPrimaryOwnerName = (primaryOwner) => {
    if (!primaryOwner) return emptyValue;

    const fullName = primaryOwner.full_name?.trim();
    if (fullName) return fullName;

    const combinedName = [primaryOwner.first_name, primaryOwner.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

    return combinedName || emptyValue;
  };

  const formatPrimaryOwnerEmail = (primaryOwner) => {
    if (!primaryOwner) return emptyValue;

    return primaryOwner.email?.trim() || emptyValue;
  };

  const openAccountStatusModal = (restaurant, event) => {
    event?.preventDefault();
    event?.stopPropagation();

    const nextStatus = getNextAccountStatus(restaurant.accountStatus);
    if (!nextStatus || updatingAccountStatusId) return;

    setAccountStatusTarget({ restaurant, nextStatus });
    setShowAccountStatusModal(true);
  };

  const closeAccountStatusModal = () => {
    if (updatingAccountStatusId) return;
    setShowAccountStatusModal(false);
    setAccountStatusTarget(null);
  };

  const renderAccountStatusToggle = (row) => {
    const isActive = row.accountStatus === "active";
    const disabled = updatingAccountStatusId === row.id;
    const actionLabel = isActive ? "Deactivate" : "Activate";

    return (
      <button
        type="button"
        className={`ar-account-toggle ${isActive ? "ar-account-toggle--active" : "ar-account-toggle--inactive"}`}
        onClick={(event) => openAccountStatusModal(row, event)}
        disabled={disabled || !row.nextAccountStatus}
        aria-pressed={isActive}
        aria-label={`${actionLabel} ${row.title}`}
        title={`${actionLabel} ${row.title}`}
      >
        <span className="ar-account-toggle__track" aria-hidden="true">
          <span className="ar-account-toggle__thumb" />
        </span>
        <span className="ar-account-toggle__label">
          {disabled ? "Updating..." : row.accountStatusLabel}
        </span>
      </button>
    );
  };

  const columns = [
    {
      key: "title",
      label: "Restaurant Name",
      width: "2fr",
      render: (row) => (
        <Link
          to={`/admin/restaurants/${row.id}/view`}
          state={{ restaurantData: row }}
          style={{
            color: '#000',
            textDecoration: 'none',
            fontWeight: '500',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.color = '#ec421c'}
          onMouseLeave={(e) => e.target.style.color = '#000'}
        >
          {row.title}
        </Link>
      )
    },
    { key: "ownerName", label: "Owner Name", width: "1.5fr" },
    { key: "ownerEmail", label: "Owner Email", width: "1.5fr" },
    { key: "address", label: "Address", width: "3fr" },
    {
      key: "accountStatus",
      label: "Account Status",
      width: "1.2fr",
      render: renderAccountStatusToggle,
    },
    { key: "linkAccount", label: "Bank Account", width: "0.8fr" },
    {
      key: "accountSetup",
      label: "Account Setup",
      width: "0.8fr",
      render: (row) => (
        <button
          type="button"
          onClick={() => handleAccountSetupClick(row)}
          style={{
            background: row.accountSetup === 'Yes' ? '#1ba97f' : '#e23d3d',
            border: 'none',
            color: '#fff',
            cursor: row.accountSetup === 'No' ? 'pointer' : 'default',
            fontWeight: '500',
            padding: '4px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            minWidth: '40px'
          }}
          disabled={row.accountSetup === 'Yes'}
        >
          {row.accountSetup}
        </button>
      )
    },
    {
      key: "actions",
      label: "",
      width: "60px",
      align: "right",
      className: "rp-table__cell--actions",
      render: (row) => (
        <ActionMenu
          isOpen={activeDropdown === row.id}
          onToggle={() => setActiveDropdown(activeDropdown === row.id ? null : row.id)}
          items={[
            {
              label: "View",
              icon: "view",
              onClick: () => navigate(`/admin/restaurants/${row.id}/view`, { state: { restaurantData: row } }),
            },
            {
              label: "Edit",
              icon: "edit",
              onClick: () => navigate(`/admin/restaurants/${row.id}/edit`, { state: { restaurantData: row } }),
            },
            {
              label: "Staff",
              icon: "users",
              onClick: () => navigate(`/admin/staff?restaurant_id=${row.id}`, { state: { restaurantId: row.id } }),
            },
            {
              label: "Delete",
              icon: "delete",
              onClick: () => handleDelete(row.id),
              danger: true,
            },
            {
              label: row.isArchive ? "Unarchive" : "Archive",
              icon: "archive",
              onClick: () => handleArchive(row.id, row.isArchive ? "unarchive" : "archive"),
            },
          ]}
        />
      ),
    },
  ];

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    
    params.append("page", pageNum);
    params.append("per_page", 20);
    params.append("has_credentials", "yes");
    params.append("sort_by", sortBy);
    params.append("sort_direction", sortDirection);
    

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const transformRestaurantData = (restaurantsArray) =>
    restaurantsArray.map((restaurant) => {
      const primaryOwner = restaurant.primary_owner || null;
      const accountStatus = getAccountStatusValue(restaurant);

      return {
        id: restaurant.id,
        title: restaurant.title || "N/A",
        legalBusinessName: restaurant.legal_business_name || "N/A",
        slug: restaurant.slug || "",
        mobile: restaurant.mobile || "N/A",
        logo: restaurant.logo || null,
        email: restaurant.email || "N/A",
        website: restaurant.website || "N/A",
        overview: restaurant.overview || "N/A",
        rating: restaurant.rating || null,
        ranking: restaurant.ranking || null,
        priceRange: restaurant.price_range || "N/A",
        status: restaurant.status,
        account_status: accountStatus,
        account_is_active: accountStatus === null ? null : accountStatus === "active",
        accountStatus,
        accountStatusLabel: getAccountStatusLabel(accountStatus),
        accountIsActive: accountStatus === null ? null : accountStatus === "active",
        nextAccountStatus: getNextAccountStatus(accountStatus),
        isArchive: restaurant.is_archive === 1,
        isMember: restaurant.is_member === "1" || restaurant.is_member === 1,
        createdAt: restaurant.created_at ? new Date(restaurant.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }) : "N/A",
        updatedAt: restaurant.updated_at || "N/A",
        userId: restaurant.user_id || null,
        bannerUrl: restaurant.banner_url || null,
        sponsorship: restaurant.sponsorship === 1,
        premium: restaurant.premium === 1,
        placeId: restaurant.place_id || null,
        availabilityHours: restaurant.availability_hours || null,
        overallRating: restaurant.overall_rating || 0,
        manuLink: restaurant.manu_link || "N/A",
        restaurantEstablish: restaurant.resturant_establish || null,
        secretMenu: restaurant.secret_menu === true,
        exclusiveDescription: restaurant.exclusive_description || null,
        open24Hours: restaurant.open_24_hours === 1,
        totalRedemptions: restaurant.total_redemptions || 0,
        latitude: restaurant.latitude || null,
        longitude: restaurant.longitude || null,
        user: restaurant.user || null,
        primaryOwner,
        address: restaurant.address?.address || "N/A",
        socialMedia: restaurant.social_media || [],
        comments: restaurant.comments || [],
        categories: restaurant.categories || [],
        categoryRating: restaurant.category_rating || [],
        restaurantRating: restaurant.resturant_rating || { total: 0, rating: null },
        city: restaurant.address?.city || "N/A",
        state: restaurant.address?.state || "N/A",
        ownerName: formatPrimaryOwnerName(primaryOwner),
        ownerEmail: formatPrimaryOwnerEmail(primaryOwner),
        linkAccount: restaurant.user?.stripe_id || restaurant.user?.pm_type ? "Yes" : "No",
        accountSetup: primaryOwner ? "Yes" : "No",
      };
    });

  const fetchRestaurants = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.adminAllRestaurants}&${buildQueryParams(pageNum)}`);

      if (response.data?.status || response.data?.success) {
        const data = response.data.data;
        const newRestaurants = data.data || [];
        const transformedRestaurants = transformRestaurantData(newRestaurants);

        if (isLoadMore) {
          setRestaurants((prev) => [...prev, ...transformedRestaurants]);
        } else {
          setRestaurants(transformedRestaurants);
        }

        const currentPage = data.current_page || 1;
        const lastPage = data.last_page || 1;
        setHasMore(currentPage < lastPage);
        setPage(currentPage);
      }
    } catch (error) {
      toastr.error("Failed to fetch restaurants");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchRestaurants(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleDelete = (restaurantId) => {
    setRestaurantToDelete(restaurantId);
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const confirmDelete = async () => {
    if (!restaurantToDelete) return;

    try {
      const response = await del(endpoints.adminDeleteRestaurant.replace("{{listing_id}}", restaurantToDelete));

      // Check for various success indicators in the response
      const isSuccess = response.data?.success || response.data?.status || response.status === 200 || response.status === 201 || response.data?.message?.toLowerCase().includes('deleted successfully') || response.data?.message?.toLowerCase().includes('deleted');
      
      if (isSuccess) {
        toastr.success("Restaurant deleted successfully");
        setShowDeleteModal(false);
        setRestaurantToDelete(null);
        fetchRestaurants(1, false);
      } else {
        toastr.error(response.data?.message || "Failed to delete restaurant");
      }
    } catch (error) {
      console.error("Failed to delete restaurant:", error);
      toastr.error(error.response?.data?.message || "Failed to delete restaurant");
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setRestaurantToDelete(null);
  };

  const handleArchive = (restaurantId, action) => {
    setRestaurantToArchive(restaurantId);
    setArchiveAction(action);
    setShowArchiveModal(true);
    setActiveDropdown(null);
  };

  const confirmArchive = async () => {
    if (!restaurantToArchive || !archiveAction) return;

    try {
      const payload = {
        id: restaurantToArchive,
        is_archived: archiveAction === "archive" ? 1 : 0,
      };
      const response = await post(endpoints.adminArchiveRestaurant, payload);
      if (response.data?.success) {
        toastr.success(archiveAction === "archive" ? "Restaurant archived successfully" : "Restaurant unarchived successfully");
        setShowArchiveModal(false);
        setRestaurantToArchive(null);
        setArchiveAction(null);
        fetchRestaurants(1, false);
      }
    } catch (error) {
      console.error("Failed to archive/unarchive restaurant:", error);
      toastr.error("Failed to update restaurant archive status");
    }
  };

  const cancelArchive = () => {
    setShowArchiveModal(false);
    setRestaurantToArchive(null);
    setArchiveAction(null);
  };

  const handleAccountSetupClick = (restaurant) => {
    if (restaurant.accountSetup === "No") {
      setRestaurantToSetup(restaurant);
      setShowAccountSetupModal(true);
    }
  };

  const handleAccountSetupConfirm = () => {
    if (restaurantToSetup) {
      setShowAccountSetupModal(false);
      navigate(`/admin/restaurants/${restaurantToSetup.id}/edit`, { state: { restaurantData: restaurantToSetup } });
      setRestaurantToSetup(null);
    }
  };

  const handleAccountSetupCancel = () => {
    setShowAccountSetupModal(false);
    setRestaurantToSetup(null);
  };

  const confirmAccountStatusUpdate = async () => {
    if (!accountStatusTarget?.restaurant?.id || !accountStatusTarget?.nextStatus || updatingAccountStatusId) return;

    const restaurantId = accountStatusTarget.restaurant.id;
    const nextStatus = accountStatusTarget.nextStatus;

    try {
      setUpdatingAccountStatusId(restaurantId);
      const response = await patchRequest(
        `/api/admin/restaurants/${restaurantId}/account-status`,
        { status: nextStatus }
      );

      if (isApiSuccess(response)) {
        const accountStatusData = extractAccountStatusData(response);
        const updatedStatus = accountStatusData.account_status || nextStatus;
        const updatedAccountIsActive = typeof accountStatusData.account_is_active !== "undefined"
          ? accountStatusData.account_is_active
          : updatedStatus === "active";
        const normalizedStatus = getAccountStatusValue({
          account_status: updatedStatus,
          account_is_active: updatedAccountIsActive,
        });

        setRestaurants((prev) => prev.map((restaurant) => (
          restaurant.id === restaurantId
            ? {
              ...restaurant,
              account_status: normalizedStatus,
              account_is_active: updatedAccountIsActive,
              accountStatus: normalizedStatus,
              accountStatusLabel: getAccountStatusLabel(normalizedStatus),
              accountIsActive: updatedAccountIsActive,
              nextAccountStatus: getNextAccountStatus(normalizedStatus),
            }
            : restaurant
        )));
        setShowAccountStatusModal(false);
        setAccountStatusTarget(null);
        toastr.success(response.data?.message || "Restaurant account status updated successfully.");
      } else {
        toastr.error(response.data?.message || "Unable to update the restaurant account status.");
      }
    } catch (error) {
      console.error("Failed to update restaurant account status:", error);
      toastr.error(extractAccountStatusErrorMessage(error));
    } finally {
      setUpdatingAccountStatusId(null);
    }
  };

  const handleExport = async () => {
    try {
      const response = await get(`${endpoints.adminExport}?type=restaurants`);
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'restaurants.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toastr.success("Export successful!");
    } catch (error) {
      console.error("Failed to export data:", error);
      const errorMessage = extractErrorMessage(error);
      toastr.error(errorMessage);
    }
  };

  const handleSortByChange = useCallback((value) => {
    setSortBy(value);
    setPage(1);
    setHasMore(true);
  }, []);

  const handleSortDirectionChange = useCallback((value) => {
    setSortDirection(value);
    setPage(1);
    setHasMore(true);
  }, []);

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: handleSortByChange,
      defaultValue: "restaurant_name",
      options: [
        { value: "restaurant_name", label: "Restaurant Name" },
        { value: "name", label: "Name" },
      ],
    },
    {
      key: "sortDirection",
      type: "sortDirection",
      label: "Sort Direction",
      value: sortDirection,
      onChange: handleSortDirectionChange,
      defaultValue: "desc",
    },
  ];
  const accountStatusConfirmation = getAccountStatusConfirmation(accountStatusTarget?.nextStatus);

  useEffect(() => {
    fetchRestaurants(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortDirection]);

  return (
    <div className="page-content rp-page-content">
      <style>{`
        .ar-account-toggle {
          align-items: center;
          background: transparent;
          border: 0;
          color: #111827;
          cursor: pointer;
          display: inline-flex;
          gap: 8px;
          min-width: 0;
          padding: 0;
        }
        .ar-account-toggle:disabled {
          cursor: not-allowed;
          opacity: 0.65;
        }
        .ar-account-toggle__track {
          align-items: center;
          background: #9ca3af;
          border-radius: 999px;
          display: inline-flex;
          flex: 0 0 auto;
          height: 18px;
          padding: 2px;
          transition: background-color 0.18s ease;
          width: 32px;
        }
        .ar-account-toggle--active .ar-account-toggle__track {
          background: #1ba97f;
        }
        .ar-account-toggle__thumb {
          background: #fff;
          border-radius: 50%;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.25);
          display: block;
          height: 14px;
          transform: translateX(0);
          transition: transform 0.18s ease;
          width: 14px;
        }
        .ar-account-toggle--active .ar-account-toggle__thumb {
          transform: translateX(14px);
        }
        .ar-account-toggle__label {
          color: #374151;
          font-size: 12px;
          font-weight: 700;
          line-height: 1.2;
          overflow-wrap: anywhere;
        }
      `}</style>
      <Container fluid>
        <div className="rp-page">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h2 className="rp-card__title" style={{ margin: 0 }}>Active Restaurants</h2>
              <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of restaurant partners with active credentials.</p>
            </div>
            <SecondaryButton
              variant="orange"
              icon={tickIcon}
              onClick={() => navigate("/admin/restaurants/create")}
            >
              Add Restaurant
            </SecondaryButton>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by name or email"
            filters={filters}
            showExport
            onExport={handleExport}
          />

          <DataTable
            columns={columns}
            rows={restaurants}
            getRowKey={(r) => r.id}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
          />
        </div>
      </Container>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Restaurant"
        message="Are you sure you want to delete this restaurant? This action cannot be undone."
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        itemName="restaurant"
      />

      {/* Archive Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showArchiveModal}
        title={archiveAction === "archive" ? "Archive Restaurant" : "Unarchive Restaurant"}
        message={archiveAction === "archive" ? "Are you sure you want to archive this restaurant?" : "Are you sure you want to unarchive this restaurant?"}
        onCancel={cancelArchive}
        onConfirm={confirmArchive}
        itemName="restaurant"
        confirmButtonText={archiveAction === "archive" ? "Archive" : "Unarchive"}
      />

      <DeleteConfirmationModal
        isOpen={showAccountStatusModal}
        title={accountStatusConfirmation.title}
        message={accountStatusConfirmation.message}
        onCancel={closeAccountStatusModal}
        onConfirm={confirmAccountStatusUpdate}
        confirmButtonText={updatingAccountStatusId ? "Updating..." : accountStatusConfirmation.confirmText}
        confirmButtonDisabled={Boolean(updatingAccountStatusId)}
        itemName="restaurant account"
      />

      {/* Account Setup Modal */}
      <DeleteConfirmationModal
        isOpen={showAccountSetupModal}
        title="Account Setup Required"
        message="This restaurant needs to complete account setup before using the OFL portal. Please add the owner details first."
        onCancel={handleAccountSetupCancel}
        onConfirm={handleAccountSetupConfirm}
        confirmButtonText="Proceed to Setup"
        itemName="account setup"
      />
    </div>
  );
};

AdminRestaurantsPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

AdminRestaurantsPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default AdminRestaurantsPage;
